import React, { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import PanelLayoutContent from "components/layout/PanelLayoutContent"
import { useNavigate, useParams } from "react-router-dom"
import { TslNewsletterService } from "services/api-workers-service"
import { SentryService } from "services/SentryService"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Button, { IconButton } from "ui/components/atoms/button"
import Spinner from "ui/components/atoms/spinner"
import Tag from "ui/components/atoms/tag"
import { H2, Label, P } from "ui/components/atoms/typography"
import Alert from "ui/components/molecules/alert"
import { formatDate } from "utils/date"

import { priorityColor, priorityLabel, statusColor, statusLabel, tagLabel } from "./data"
import TslNewsletterNav from "./TslNewsletterNav"

const COLLAPSE_THRESHOLD = 3000

const ArticleDetail = () => {
  const { articleId = "" } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionInfo, setActionInfo] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  const { data: article, isLoading } = useQuery({
    queryKey: ["tsl", "article", articleId],
    queryFn: () => TslNewsletterService.getArticle(articleId),
    enabled: !!articleId,
  })

  const { data: source } = useQuery({
    queryKey: ["tsl", "source", article?.source_id],
    queryFn: async () => {
      const list = await TslNewsletterService.listSources({ limit: 200 })
      return list.items.find(s => s.id === article?.source_id) ?? null
    },
    enabled: !!article?.source_id,
  })

  const reprocessMutation = useMutation({
    mutationFn: () => TslNewsletterService.reprocessArticle(articleId),
    onSuccess: () => {
      setActionInfo("Artykuł przetworzony na nowo.")
      queryClient.invalidateQueries({ queryKey: ["tsl", "article", articleId] })
      queryClient.invalidateQueries({ queryKey: ["tsl", "articles"] })
    },
    onError: (e: any) => {
      if (e?.response?.status === 422) {
        setActionError("Nie udało się pobrać treści artykułu ze źródła — sprawdź URL artykułu.")
        return
      }
      SentryService.error("[ERROR TslNewsletterService.reprocessArticle]:", e)
      setActionError(e?.response?.data?.detail || "Nie udało się przetworzyć artykułu.")
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => TslNewsletterService.deleteArticle(articleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tsl", "articles"] })
      navigate("/panel/tsl-newsletter/articles")
    },
    onError: e => {
      SentryService.error("[ERROR TslNewsletterService.deleteArticle]:", e)
      setActionError("Nie udało się usunąć artykułu.")
    },
  })

  if (isLoading) {
    return (
      <PanelLayoutContent title="TSL Newsletter" text="Szczegóły artykułu">
        <TslNewsletterNav />
        <Row $justifyContent="center" $padding="M">
          <Spinner $size="30px" />
        </Row>
      </PanelLayoutContent>
    )
  }

  if (!article) {
    return (
      <PanelLayoutContent title="TSL Newsletter" text="Szczegóły artykułu">
        <TslNewsletterNav />
        <Alert $type="error">Nie znaleziono artykułu.</Alert>
      </PanelLayoutContent>
    )
  }

  const sc = statusColor(article.status)
  const pc = priorityColor(article.priority)
  const content = article.content_md || ""
  const longContent = content.length > COLLAPSE_THRESHOLD
  const visibleContent =
    longContent && !expanded ? `${content.slice(0, COLLAPSE_THRESHOLD)}…` : content

  return (
    <PanelLayoutContent title="TSL Newsletter" text="Szczegóły artykułu">
      <TslNewsletterNav />

      <Row $alignItems="center" $marginBottom="M">
        <IconButton
          $background="background"
          $size="45px"
          $iconSize="L"
          $borderRadius="borderRadius"
          onClick={() => navigate("/panel/tsl-newsletter/articles")}
          icon="arrow-left"
          $marginRight="S"
          ariaLabel="Wstecz"
        />
        <H2 $marginBottom="0">{article.title || "(brak tytułu)"}</H2>
      </Row>

      {actionError && (
        <Alert $type="error" $small $marginBottom="S">
          {actionError}
        </Alert>
      )}
      {actionInfo && (
        <Alert $type="success" $small $marginBottom="S">
          {actionInfo}
        </Alert>
      )}

      <Layout>
        <Main>
          {article.image_url && (
            <Image
              src={article.image_url}
              alt=""
              onError={e => {
                e.currentTarget.style.display = "none"
              }}
            />
          )}

          <Row $alignItems="center" $marginBottom="S">
            <Tag
              $type="secondary"
              $showBorder={false}
              $background={sc.bg}
              $color={sc.color}
              label={statusLabel(article.status)}
              $marginRight="XS"
            />
            <Tag
              $type="secondary"
              $showBorder={false}
              $background={pc.bg}
              $color={pc.color}
              label={`P${article.priority ?? "?"} ${priorityLabel(article.priority)}`}
            />
          </Row>

          {article.tags?.length > 0 && (
            <TagsRow $marginBottom="M">
              {article.tags.map(t => (
                <Tag
                  key={t}
                  $type="secondary"
                  $showBorder={false}
                  $background="background"
                  label={tagLabel(t)}
                />
              ))}
            </TagsRow>
          )}

          {content ? (
            <ContentBox>
              <Pre>{visibleContent}</Pre>
              {longContent && (
                <Button $type="secondary" $marginTop="S" onClick={() => setExpanded(!expanded)}>
                  {expanded ? "Zwiń" : "Pokaż całość"}
                </Button>
              )}
            </ContentBox>
          ) : (
            <Alert $type="warning" $small>
              Brak treści (content_md). Ponów fetch po stronie backendu.
            </Alert>
          )}
        </Main>

        <Sidebar>
          <SidebarBox>
            <Label $type="secondary" $marginBottom="XS">
              Źródło
            </Label>
            <P $marginBottom="XXS">{source?.name ?? "—"}</P>
            {source?.domain && (
              <a
                href={`https://${source.domain}`}
                target="_blank"
                rel="noreferrer"
                style={{ color: "inherit", textDecoration: "underline" }}
              >
                <P $small $marginBottom="0">
                  {source.domain}
                </P>
              </a>
            )}
          </SidebarBox>

          <SidebarBox>
            <Label $type="secondary" $marginBottom="XS">
              URL kanoniczny
            </Label>
            <a
              href={article.url_canonical}
              target="_blank"
              rel="noreferrer"
              style={{ color: "inherit", textDecoration: "underline" }}
            >
              <P $small $marginBottom="0">
                Otwórz oryginał
              </P>
            </a>
          </SidebarBox>

          <SidebarBox>
            <Label $type="secondary" $marginBottom="XS">
              Daty
            </Label>
            {article.published_at && (
              <P $small $marginBottom="XXS">
                Publikacja: {formatDate(article.published_at)}
              </P>
            )}
            <P $small $marginBottom="XXS">
              Utworzono: {formatDate(article.created_at)}
            </P>
            <P $small $marginBottom="0">
              Aktualizacja: {formatDate(article.updated_at)}
            </P>
          </SidebarBox>

          <SidebarBox>
            <Button
              icon="boost"
              loading={reprocessMutation.isPending}
              onClick={() => {
                if (
                  !window.confirm("Ponownie przetworzyć przez AI? Zastąpi tagi, priority, summary.")
                )
                  return
                setActionError(null)
                setActionInfo(null)
                reprocessMutation.mutate()
              }}
              disabled={article.status === "deleted"}
              $marginBottom="XS"
            >
              Reprocess AI
            </Button>
            <Button
              $type="secondary"
              icon="trash"
              loading={deleteMutation.isPending}
              onClick={() => {
                if (
                  !window.confirm(
                    "Oznaczyć artykuł jako usunięty? Pominięty w wysyłce newslettera.",
                  )
                )
                  return
                setActionError(null)
                setActionInfo(null)
                deleteMutation.mutate()
              }}
              disabled={article.status === "deleted"}
            >
              Usuń artykuł
            </Button>
          </SidebarBox>
        </Sidebar>
      </Layout>
    </PanelLayoutContent>
  )
}

export default ArticleDetail

const Layout = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: ${({ theme }) => theme.space.M};

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`

const Main = styled(Box)`
  background: white;
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  padding: ${({ theme }) => theme.space.S};
`

const Sidebar = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.S};
`

const SidebarBox = styled(Box)`
  background: white;
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  padding: ${({ theme }) => theme.space.S};
`

const Image = styled.img`
  width: 100%;
  max-height: 360px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  margin-bottom: ${({ theme }) => theme.space.S};
  background: ${({ theme }) => theme.color.background};
`

const ContentBox = styled(Box)`
  background: ${({ theme }) => theme.color.background};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  padding: ${({ theme }) => theme.space.S};
`

const Pre = styled.pre`
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
`

const TagsRow = styled(Row)`
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.XXS};
`
